import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
  Paper,
  Chip
} from '@mui/material';
import { Alert } from '@mui/material';
import { CheckCircle, Error as ErrorIcon, FolderOpen, HourglassEmpty } from '@mui/icons-material';
import projectService from '../../services/projectService';

type BulkItem = {
  package_symbol: string;
  name: string;
  stage: string;
  package_type?: string;
  description?: string;
  parameters?: string;
  estimated_value?: number;
};

type RowState = {
  data: BulkItem;
  files: File[];
  status: 'pending' | 'processing' | 'done' | 'error';
  error?: string;
  summary?: string;
};

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: string;
  initialPackages: BulkItem[];
  onCompleted?: (summary: { successes: number; failures: number; total: number }) => void;
}

const basename = (p?: string) => {
  if (!p) return '';
  const s = p.includes('\\') ? p.split('\\') : p.split('/');
  return s[s.length - 1] || p;
};

const truncate = (v?: string, n: number = 15) => {
  if (!v) return '';
  return v.length > n ? `${v.slice(0, n)}…` : v;
};

const formatPln = (v?: number) => {
  if (v === undefined || v === null) return '';
  try { return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN', maximumFractionDigits: 0 }).format(v); } catch { return `${v} PLN`; }
};

async function collectFilesFromDirectoryHandle(dirHandle: any): Promise<File[]> {
  const out: File[] = [];
  // @ts-ignore
  for await (const entry of dirHandle.values ? dirHandle.values() : dirHandle.entries()) {
    const handle = entry.kind ? entry : entry[1];
    if (handle.kind === 'file') {
      const file = await handle.getFile();
      out.push(file);
    } else if (handle.kind === 'directory') {
      const nested = await collectFilesFromDirectoryHandle(handle);
      out.push(...nested);
    }
  }
  return out;
}

const BulkImportPackagesDialog: React.FC<Props> = ({ open, onClose, projectId, initialPackages, onCompleted }) => {
  const [rows, setRows] = useState<RowState[]>(() =>
    (initialPackages || []).map((p) => ({ data: p, files: [], status: 'pending' }))
  );
  const [running, setRunning] = useState(false);
  const [successes, setSuccesses] = useState(0);
  const [failures, setFailures] = useState(0);
  const [existingSymbols, setExistingSymbols] = useState<Set<string>>(new Set());
  const dirInputRef = useRef<HTMLInputElement>(null);
  const [dirPickIndex, setDirPickIndex] = useState<number | null>(null);

  const totalDone = useMemo(() => rows.filter((r) => r.status === 'done').length, [rows]);

  // Aktualizuj listę po otwarciu lub zmianie danych
  useEffect(() => {
    if (open) {
      setRows((initialPackages || []).map((p) => ({ data: p, files: [], status: 'pending' })));
      setSuccesses(0);
      setFailures(0);
    }
  }, [open, initialPackages]);

  // Pobierz istniejące symbole pakietów w projekcie, aby nie importować duplikatów
  useEffect(() => {
    if (!open || !projectId) return;
    (async () => {
      try {
        const pkgs = await projectService.getPackagesByProjectId(projectId);
        const s = new Set<string>((pkgs || []).map((p: any) => (p.package_symbol || '').toString().trim().toLowerCase()));
        setExistingSymbols(s);
      } catch {}
    })();
  }, [open, projectId]);

  const handlePickFolder = async (index: number) => {
    try {
      // @ts-ignore
      if (window.showDirectoryPicker) {
        // @ts-ignore
        const handle = await window.showDirectoryPicker();
        const files = await collectFilesFromDirectoryHandle(handle);
        applyPickedFiles(index, files);
        return;
      }
    } catch (e) {
      // fallback
    }
    setDirPickIndex(index);
    dirInputRef.current?.click();
  };

  const applyPickedFiles = (index: number, picked: File[]) => {
    const matched = picked; // JSON nie zawiera listy plików – bierzemy wszystko z wybranego folderu
    const totalBytes = matched.reduce((acc, f) => acc + (f.size || 0), 0);
    const totalMB = totalBytes / (1024 * 1024);
    const summary = `Wybrano ${matched.length} • ${totalMB.toFixed(1)} MB`;
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, files: matched, summary } : r)));
  };

  const handleDirInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (dirPickIndex === null) return;
    const fl = e.target.files ? Array.from(e.target.files) : [];
    applyPickedFiles(dirPickIndex, fl);
    setDirPickIndex(null);
  };

  const convertToApiPayload = (it: BulkItem) => ({
    project_id: projectId,
    package_symbol: it.package_symbol,
    name: it.name,
    stage: it.stage || 'Etap 1',
    description: it.description || '',
    parameters: it.parameters || '',
    estimated_value: typeof it.estimated_value === 'number' ? it.estimated_value : undefined,
    package_type: it.package_type || undefined,
    // znacznik źródła AI – backend nie zapisze go do kolumny, ale możemy użyć do UI jeśli kiedyś będzie potrzebny
    __source: 'AI'
  });

  const runImport = async () => {
    setRunning(true);
    setSuccesses(0);
    setFailures(0);
    try {
      // lokalna kopia zbioru symboli – rozszerzamy o nowo utworzone, aby uniknąć duplikatów w tym samym imporcie
      const usedSymbols = new Set(existingSymbols);
      for (let i = 0; i < rows.length; i++) {
        setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, status: 'processing', error: undefined } : r)));
        const row = rows[i];
        try {
          const symbol = (row.data.package_symbol || '').toString().trim().toLowerCase();
          if (!symbol) {
            throw new Error('Brak symbolu pakietu');
          }
          if (usedSymbols.has(symbol)) {
            // duplikat – pomiń
            setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, status: 'error', error: 'Pakiet o tym symbolu już istnieje – pominięto' } : r)));
            setFailures((f) => f + 1);
            continue;
          }
          const payload: any = convertToApiPayload(row.data);
          const files = row.files && row.files.length > 0 ? row.files : undefined;
          await projectService.createPackage({ ...payload, files } as any);
          setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, status: 'done' } : r)));
          setSuccesses((s) => s + 1);
          usedSymbols.add(symbol);
        } catch (e: any) {
          setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, status: 'error', error: e?.message || 'Błąd' } : r)));
          setFailures((f) => f + 1);
          // kontynuuj następne, zgodnie z wymaganiem sekwencji
        }
      }
      const summary = { successes: successes, failures: failures, total: rows.length };
      onCompleted && onCompleted(summary);
    } finally {
      setRunning(false);
      // Zamknij okno po imporcie
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={running ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import pakietów z JSON ({rows.length})</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Wskaż folder z plikami dla każdego pakietu (opcjonalnie). Zapis uruchomi tworzenie po kolei.
        </Typography>
        {running && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="info" sx={{ mb: 1 }}>Trwa import pakietów… Proszę nie zamykać tego okna.</Alert>
            <LinearProgress />
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>{`Postęp: ${successes + failures}/${rows.length} · Sukcesy: ${successes} · Błędy: ${failures}`}</Typography>
          </Box>
        )}
        <Paper variant="outlined" sx={{ maxHeight: 420, overflow: 'auto' }}>
          <List dense>
            {rows.map((row, idx) => (
              <ListItem key={idx} divider>
                <ListItemText
                  primary={`${row.data.package_symbol} — ${row.data.name}`}
                  secondary={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {`${row.data.stage}${row.data.package_type ? ` · ${row.data.package_type}` : ''}`}
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1.2fr 1.2fr auto auto',
                          alignItems: 'center',
                          gap: 1,
                          pr: 2
                        }}
                      >
                        <Tooltip title={row.data.description || ''} placement="bottom-start">
                          <Typography variant="caption">Opis: {truncate(row.data.description, 15) || '-'}</Typography>
                        </Tooltip>
                        <Tooltip title={row.data.parameters || ''} placement="bottom-start">
                          <Typography variant="caption">Parametry: {truncate(row.data.parameters, 15) || '-'}</Typography>
                        </Tooltip>
                        <Typography variant="caption">
                          {typeof row.data.estimated_value === 'number'
                            ? `Szacowana wartość: ${formatPln(row.data.estimated_value)}`
                            : 'Szacowana wartość: -'}
                        </Typography>
                      </Box>
                      {row.summary && (
                        <Box sx={{ mt: 0.5 }}>
                          <Chip size="small" color="success" variant="outlined" label={row.summary} sx={{ height: 20, fontSize: '0.70rem', width: 'fit-content' }} />
                        </Box>
                      )}
                    </Box>
                  }
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
                  {row.status === 'done' && <Tooltip title="Ukończono"><CheckCircle color="success" fontSize="small" /></Tooltip>}
                  {row.status === 'processing' && <Tooltip title="W trakcie"><HourglassEmpty color="primary" fontSize="small" /></Tooltip>}
                  {row.status === 'error' && <Tooltip title={row.error || 'Błąd'}><ErrorIcon color="error" fontSize="small" /></Tooltip>}
                </Box>
                <ListItemSecondaryAction>
                  <IconButton onClick={() => handlePickFolder(idx)} edge="end" size="small">
                    <FolderOpen />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>
        <DialogActions>
          <Button onClick={onClose} disabled={running}>
            Anuluj
          </Button>
          <Button onClick={runImport} variant="contained" disabled={running}>
            {running ? <HourglassEmpty /> : <CheckCircle />} Importuj
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default BulkImportPackagesDialog;